import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categoriesAPI, getImageUrl } from '../utils/api';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ left: 0, width: 0 });
  const location = useLocation();
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownTimeoutRef = useRef(null);
  const [activeCategory, setActiveCategory] = useState(null);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        left: rect.left,
        width: rect.width
      });
    }
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 200); // 200ms delay before closing
  };

  const handleDropdownMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setDropdownOpen(true);
  };

  const handleDropdownMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 200);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        buttonRef.current && !buttonRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);

      // Update dropdown position on scroll
      if (dropdownOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          left: rect.left,
          width: rect.width
        });
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [dropdownOpen]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoriesAPI.getAll({ status: 'active' });
        const data = Array.isArray(response.data) ? response.data : (response.data?.categories || response.data?.data || []);
        setCategories(data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  const getCategoryIcon = (categoryName) => {
    const name = categoryName?.toLowerCase() || '';
    if (name.includes('barcode printer')) return 'fas fa-barcode';
    if (name.includes('thermal printer')) return 'fas fa-print';
    if (name.includes('label')) return 'fas fa-tag';
    if (name.includes('roll')) return 'fas fa-scroll';
    return 'fas fa-box';
  };

  const getCategoryColor = (categoryName) => {
    const name = categoryName?.toLowerCase() || '';
    if (name.includes('barcode printer')) return 'text-indigo-600';
    if (name.includes('thermal printer')) return 'text-purple-600';
    if (name.includes('label')) return 'text-green-600';
    if (name.includes('roll')) return 'text-teal-600';
    return 'text-gray-600';
  };

  // ✅ FIXED: Safe logo error handler
  const handleLogoError = (e) => {
    const img = e.target;
    const fallback = img.nextSibling;
    if (fallback && fallback.style) {
      img.style.display = 'none';
      fallback.style.display = 'flex';
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-md shadow-md h-16' : location.pathname === '/' ? 'bg-transparent h-20' : 'bg-white/90 backdrop-blur-md shadow-sm h-16'
          }`}
        style={{
          width: '100%',
          overflowX: 'hidden'
        }}
      >
        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center flex-shrink-0">
              <Link to="/" className="flex items-center space-x-3 group/logo" data-aos="fade-right">
                <div className="relative flex-shrink-0">
                  <img
                    src="/logo.png"
                    alt="Digital Barcode Logo"
                    className="h-12 sm:h-16 w-auto transition-all duration-300 group-hover/logo:scale-110"
                    style={{
                      filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.1))'
                    }}
                    onError={handleLogoError}
                  />
                  {/* ✅ FALLBACK TEXT LOGO - Only shows if image fails */}
                  <div
                    className="hidden flex-col items-center justify-center font-bold text-xl sm:text-2xl text-gray-800 bg-white/80 px-4 py-2 rounded-lg shadow-md backdrop-blur-sm"
                    style={{ minWidth: '120px', minHeight: '48px' }}
                  >
                    <div className="text-xs sm:text-sm leading-tight text-gray-700">DIGITAL</div>
                    <div className="text-xs sm:text-sm leading-tight">BARCODE</div>
                  </div>
                </div>
              </Link>
            </div>

            <div className="hidden md:flex items-center space-x-1 lg:space-x-2 absolute left-1/2 transform -translate-x-1/2">
              <Link
                to="/"
                className={`relative px-4 py-2 transition-all duration-300 text-sm font-semibold tracking-wide group overflow-hidden ${location.pathname === '/'
                  ? 'text-blue-600'
                  : 'text-gray-700 hover:text-blue-600'
                  }`}
              >
                <span className="relative z-10 flex items-center">
                  <i className={`fas fa-home mr-2 text-xs ${location.pathname === '/' ? 'text-blue-600' : 'text-blue-500'}`}></i>
                  Home
                </span>
                {location.pathname !== '/' && (
                  <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 group-hover:w-3/4 group-hover:left-[12.5%] transition-all duration-300"></span>
                )}
              </Link>

              <Link
                to="/about"
                className={`relative px-4 py-2 transition-all duration-300 text-sm font-semibold tracking-wide group overflow-hidden ${location.pathname === '/about'
                  ? 'text-blue-600'
                  : 'text-gray-700 hover:text-blue-600'
                  }`}
              >
                <span className="relative z-10 flex items-center">
                  <i className={`fas fa-building mr-2 text-xs ${location.pathname === '/about' ? 'text-blue-600' : 'text-blue-500'}`}></i>
                  About
                </span>
                {location.pathname !== '/about' && (
                  <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 group-hover:w-3/4 group-hover:left-[12.5%] transition-all duration-300"></span>
                )}
              </Link>

              {/* Categories Dropdown Trigger */}
              <div
                className="relative"
                ref={buttonRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <Link
                  to="/products"
                  className={`relative px-4 py-2 transition-all duration-300 flex items-center text-sm font-semibold tracking-wide group overflow-hidden ${dropdownOpen || location.pathname === '/products'
                    ? 'text-blue-600'
                    : 'text-gray-700 hover:text-blue-600'}`}
                >
                  <span className="relative z-10 flex items-center">
                    <i className={`fas fa-box-open mr-2 text-xs ${dropdownOpen || location.pathname === '/products' ? 'text-blue-600' : 'text-blue-500'}`}></i>
                    Products
                    <i className={`fas fa-chevron-down ml-2 text-[10px] transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                  </span>
                  {location.pathname !== '/products' && !dropdownOpen && (
                    <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 group-hover:w-3/4 group-hover:left-[12.5%] transition-all duration-300"></span>
                  )}
                </Link>
              </div>

              <Link
                to="/certifications"
                className={`relative px-4 py-2 transition-all duration-300 text-sm font-semibold tracking-wide group overflow-hidden ${location.pathname === '/certifications'
                  ? 'text-blue-600'
                  : 'text-gray-700 hover:text-blue-600'
                  }`}
              >
                <span className="relative z-10 flex items-center">
                  <i className={`fas fa-certificate mr-2 text-xs ${location.pathname === '/certifications' ? 'text-blue-600' : 'text-blue-500'}`}></i>
                  Certifications
                </span>
                {location.pathname !== '/certifications' && (
                  <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 group-hover:w-3/4 group-hover:left-[12.5%] transition-all duration-300"></span>
                )}
              </Link>

              <Link
                to="/contact"
                className={`relative px-4 py-2 transition-all duration-300 text-sm font-semibold tracking-wide group overflow-hidden ${location.pathname === '/contact'
                  ? 'text-blue-600'
                  : 'text-gray-700 hover:text-blue-600'
                  }`}
              >
                <span className="relative z-10 flex items-center">
                  <i className={`fas fa-envelope mr-2 text-xs ${location.pathname === '/contact' ? 'text-blue-600' : 'text-blue-500'}`}></i>
                  Contact
                </span>
                {location.pathname !== '/contact' && (
                  <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 group-hover:w-3/4 group-hover:left-[12.5%] transition-all duration-300"></span>
                )}
              </Link>
            </div>




            {/* CTA Button */}
            <div className="hidden md:flex items-center gap-4 flex-shrink-0 mr-8 md:mr-12 lg:mr-16">
              <Link to="/quote">
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-full text-sm font-bold shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5">
                  Get Quote
                </button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-gray-700 p-2 rounded-xl hover:bg-gray-100 transition-all duration-300 relative flex-shrink-0"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'} text-xl transition-transform duration-300`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-gray-200 shadow-lg">
            <div className="px-6 py-4 space-y-2 max-h-[80vh] overflow-y-auto">
              <Link
                to="/"
                className={`flex items-center px-4 py-3 rounded-xl transition-all duration-300 font-medium ${location.pathname === '/' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                onClick={toggleMobileMenu}
              >
                Home
              </Link>
              <Link
                to="/about"
                className={`flex items-center px-4 py-3 rounded-xl transition-all duration-300 font-medium ${location.pathname === '/about' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                onClick={toggleMobileMenu}
              >
                About Us
              </Link>
              <div className="border-t border-gray-200 my-3 pt-3">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-2 py-2 mb-3">Categories</div>
                {loadingCategories ? (
                  <div className="px-4 py-6 text-center text-gray-500">
                    <i className="fas fa-spinner fa-spin text-lg mb-2"></i>
                    <span className="text-sm">Loading...</span>
                  </div>
                ) : (
                  <>
                    <Link
                      to="/categories"
                      className="flex items-center px-4 py-3 rounded-xl hover:bg-gray-50 transition-all duration-300 text-gray-700 font-medium"
                      onClick={toggleMobileMenu}
                    >
                      All Categories
                    </Link>
                    {categories.map((category, index) => (
                      <Link
                        key={category._id || index}
                        to={`/categories/${category.slug || category._id}`}
                        className="flex items-center justify-between px-4 py-3 rounded-xl hover:bg-gray-50 transition-all duration-300 text-gray-700"
                        onClick={toggleMobileMenu}
                      >
                        <span className="font-medium">{category.name}</span>
                        {category.productCount > 0 && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full font-semibold">
                            {category.productCount}
                          </span>
                        )}
                      </Link>
                    ))}
                  </>
                )}
              </div>
              <Link
                to="/certifications"
                className="flex items-center px-4 py-3 rounded-xl hover:bg-gray-50 transition-all duration-300 text-gray-700 font-medium"
                onClick={toggleMobileMenu}
              >
                Certifications
              </Link>
              <Link
                to="/contact"
                className="flex items-center px-4 py-3 rounded-xl hover:bg-gray-50 transition-all duration-300 text-gray-700 font-medium"
                onClick={toggleMobileMenu}
              >
                Contact Us
              </Link>
              <Link
                to="/quote"
                className="block w-full"
                onClick={toggleMobileMenu}
              >
                <button
                  className="w-full bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 hover:from-blue-700 hover:via-blue-600 hover:to-blue-700 px-6 py-3.5 rounded-xl transition-all duration-300 font-semibold text-white shadow-lg hover:shadow-xl"
                >
                  Request Proposal
                </button>
              </Link>
            </div>
          </div>
        )}
      </nav >

      {/* Categories Dropdown */}
      {/* Categories Dropdown */}
      {
        dropdownOpen && buttonRef.current && (
          <div
            ref={dropdownRef}
            className="fixed z-50 flex shadow-2xl rounded-2xl overflow-hidden bg-white/95 backdrop-blur-2xl ring-1 ring-gray-900/10 transform transition-all duration-300 origin-top-left"
            onMouseEnter={handleDropdownMouseEnter}
            onMouseLeave={handleDropdownMouseLeave}
            style={{
              top: `${buttonRef.current.getBoundingClientRect().bottom + 20}px`,
              left: `${buttonRef.current.getBoundingClientRect().left - 100}px`, // Slight offset to center relative to button area
              maxHeight: 'calc(100vh - 120px)',
            }}
          >
            {/* Categories List (Left Side) */}
            <div className="w-72 bg-gray-50/50 flex flex-col border-r border-gray-100">
              {loadingCategories ? (
                <div className="flex flex-col items-center justify-center flex-1 text-gray-400">
                  <i className="fas fa-spinner fa-spin text-2xl mb-3"></i>
                  <p className="text-sm font-medium">Loading...</p>
                </div>
              ) : categories.length === 0 ? (
                <div className="flex flex-col items-center justify-center flex-1 text-gray-400">
                  <i className="fas fa-box-open text-2xl mb-3"></i>
                  <p className="text-sm font-medium">No categories</p>
                </div>
              ) : (
                <>
                  <div className="p-4 pb-2 flex-shrink-0 z-10 bg-gray-50/50">
                    <Link
                      to="/categories"
                      className={`group flex items-center p-3 rounded-xl transition-all duration-300 ${!activeCategory ? 'bg-white shadow-sm ring-1 ring-gray-200' : 'hover:bg-white/60 hover:shadow-sm'}`}
                      onMouseEnter={() => setActiveCategory(null)}
                      onClick={() => setDropdownOpen(false)}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 transition-colors ${!activeCategory ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 group-hover:text-blue-600'}`}>
                        <i className="fas fa-th-large text-sm"></i>
                      </div>
                      <div className="flex-1">
                        <span className={`font-semibold block ${!activeCategory ? 'text-gray-900' : 'text-gray-600 group-hover:text-gray-900'}`}>All Categories</span>
                        <span className="text-xs text-gray-400">View full catalog</span>
                      </div>
                      <i className="fas fa-chevron-right text-xs text-gray-300"></i>
                    </Link>
                    <div className="h-px bg-gray-200/60 mt-3 mx-1"></div>
                  </div>

                  <div className="flex-1 overflow-y-auto px-4 pb-4 custom-scrollbar space-y-1">
                    {categories.filter(c => !c.parentCategory).map((category) => {
                      const isActive = activeCategory?._id === category._id;
                      const iconClass = getCategoryIcon(category.name);
                      const colorClass = getCategoryColor(category.name);
                      /* Extract base color from text class e.g. text-blue-600 -> blue */
                      const colorName = colorClass.replace('text-', '').replace('-600', '');

                      return (
                        <Link
                          key={category._id}
                          to={`/categories/${category.slug || category._id}`}
                          className={`group flex items-center p-3 rounded-xl transition-all duration-300 ${isActive ? 'bg-white shadow-sm ring-1 ring-gray-200' : 'hover:bg-white/60 hover:shadow-sm'}`}
                          onMouseEnter={() => setActiveCategory(category)}
                          onClick={() => setDropdownOpen(false)}
                        >
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 transition-colors ${isActive ? `bg-${colorName}-100 ${colorClass}` : 'bg-gray-100 text-gray-400 group-hover:text-gray-600'}`}>
                            <i className={`${iconClass} text-sm`}></i>
                          </div>
                          <div className="flex-1">
                            <span className={`font-medium block ${isActive ? 'text-gray-900' : 'text-gray-600 group-hover:text-gray-900'}`}>{category.name}</span>
                            {category.productCount > 0 && (
                              <span className="text-xs text-gray-400">{category.productCount} products</span>
                            )}
                          </div>
                          {isActive && <i className={`fas fa-chevron-right text-xs ${colorClass}`}></i>}
                        </Link>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Products Preview (Right Side) */}
            <div className="w-[500px] bg-white p-6 flex flex-col">
              {activeCategory ? (
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg flex items-center">
                        {activeCategory.name}
                        <span className="ml-3 text-xs font-normal bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Featured</span>
                      </h3>
                    </div>
                    <Link
                      to={`/categories/${activeCategory.slug || activeCategory._id}`}
                      className="text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline flex items-center"
                      onClick={() => setDropdownOpen(false)}
                    >
                      View All <i className="fas fa-arrow-right ml-1 text-xs"></i>
                    </Link>
                  </div>

                  <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                    {/* Check if this category has sub-categories */}
                    {(() => {
                      const subCats = categories.filter(c => c.parentCategory && (c.parentCategory._id === activeCategory._id || c.parentCategory === activeCategory._id));

                      // If has Sub-Categories, show them!
                      if (subCats.length > 0) {
                        return (
                          <div>
                            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Sub Categories</h4>
                            <div className="grid grid-cols-2 gap-4">
                              {subCats.map(sub => (
                                <Link
                                  key={sub._id}
                                  to={`/categories/${sub.slug || sub._id}`}
                                  className="block p-4 rounded-xl bg-gray-50 hover:bg-blue-50 border border-gray-100 hover:border-blue-100 transition-all group/sub"
                                  onClick={() => setDropdownOpen(false)}
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="font-semibold text-gray-900 group-hover/sub:text-blue-700">{sub.name}</span>
                                    <i className="fas fa-arrow-right text-xs text-blue-300 group-hover/sub:text-blue-500 opacity-0 group-hover/sub:opacity-100 transition-opacity"></i>
                                  </div>
                                  <p className="text-xs text-gray-500">{sub.productCount} Products</p>
                                </Link>
                              ))}
                            </div>

                            {activeCategory.products && activeCategory.products.length > 0 && (
                              <div className="mt-6 pt-6 border-t border-gray-100">
                                <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Direct Products</h4>
                                {/* Standard Product Grid Logic for direct products */}
                                <div className="grid grid-cols-2 gap-4">
                                  {activeCategory.products.slice(0, 4).map((product) => (
                                    <Link
                                      key={product._id}
                                      to={`/product/${product._id}`}
                                      className="group/card flex bg-white rounded-lg p-2 border border-gray-100 hover:shadow-md transition-all"
                                      onClick={(e) => {
                                        console.log('Menu click direct:', product.name, product._id);
                                        // Delay closing slightly to ensure navigation registers or just purely debug
                                        setDropdownOpen(false);
                                      }}
                                    >
                                      <div className="w-12 h-12 bg-gray-50 rounded flex items-center justify-center flex-shrink-0">
                                        {product.images && product.images[0] ? (
                                          <img src={getImageUrl(product.images[0])} alt={product.name} className="max-w-full max-h-full object-contain" />
                                        ) : (
                                          <i className="fas fa-image text-gray-300"></i>
                                        )}
                                      </div>
                                      <div className="ml-3 min-w-0">
                                        <h4 className="text-xs font-semibold text-gray-900 line-clamp-2">{product.name}</h4>
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }

                      // Otherwise show Products as before
                      if (activeCategory.products && activeCategory.products.length > 0) {
                        return (
                          <div className="grid grid-cols-2 gap-4">
                            {activeCategory.products.slice(0, 6).map((product) => (
                              <Link
                                key={product._id}
                                to={`/product/${product._id}`}
                                className="group/card flex bg-gray-50 hover:bg-white rounded-xl p-3 border border-transparent hover:border-gray-200 hover:shadow-lg transition-all duration-300"
                                onClick={() => { console.log('Menu click standard:', product.name, product._id); setDropdownOpen(false); }}
                              >
                                <div className="w-16 h-16 bg-white rounded-lg p-2 flex items-center justify-center border border-gray-100 group-hover/card:border-blue-100 flex-shrink-0">
                                  {product.images && product.images[0] ? (
                                    <img src={getImageUrl(product.images[0])} alt={product.name} className="max-w-full max-h-full object-contain" />
                                  ) : (
                                    <i className="fas fa-image text-gray-300"></i>
                                  )}
                                </div>
                                <div className="ml-3 flex-1 min-w-0">
                                  <h4 className="text-sm font-semibold text-gray-900 line-clamp-2 leading-tight group-hover/card:text-blue-600 transition-colors">
                                    {product.name}
                                  </h4>
                                  <p className="text-xs text-gray-500 mt-1 line-clamp-1">{product.sku}</p>
                                </div>
                              </Link>
                            ))}
                          </div>
                        );
                      }

                      return (
                        <div className="flex flex-col items-center justify-center h-full text-gray-400">
                          <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                            <i className="fas fa-box-open text-2xl opacity-50"></i>
                          </div>
                          <p className="text-sm">No products found in this category</p>
                        </div>
                      );
                    })()}

                  </div>

                  {/* Banner / Footer in dropdown */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 flex items-center justify-between">
                      <div>
                        <p className="text-xs font-bold text-blue-800 uppercase tracking-wide">Need Help?</p>
                        <p className="text-xs text-blue-600 mt-0.5">Our experts can help you choose.</p>
                      </div>
                      <Link to="/contact" className="bg-white text-blue-600 text-xs font-bold px-3 py-2 rounded-lg shadow-sm hover:shadow-md transition-all">
                        Contact Sales
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center p-8">
                  <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mb-6 animate-pulse">
                    <i className="fas fa-search text-3xl text-blue-500"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Explore Our Catalog</h3>
                  <p className="text-gray-500 text-sm max-w-xs mx-auto">
                    Hover over the categories on the left to preview our latest industrial printers, scanners, and supplies.
                  </p>
                </div>
              )}
            </div>
          </div>
        )
      }
    </>
  );
};

export default Navbar;
